// Deterministic, render-time PII anonymisation for demo mode.
//
// Pure + stable: the same real value always maps to the same fake one (via an
// FNV-1a hash seed), so the demo reads coherently across pages (the same
// landlord shows the same fake name everywhere). Applied at the single API
// chokepoint (useAgentsHttp) so every response shape is covered at once.
//
// Safety: only a known set of PII string keys is rewritten; every other key -
// crucially the ids used for routing (project_id, run_id, upload_id) and the
// numbers/booleans - passes through untouched, so anonymisation can never
// break navigation. See docs/Y2026-Q2/design-demo-mode.md for the full ruleset.

const DEMO_USER = "Demo User";
const NOTES_PLACEHOLDER = "Sample notes (hidden in demo mode)";

// FNV-1a -> uint32. Tiny, dependency-free, good enough spread for picking a
// stable stand-in per real value.
const hash = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

const pick = <T>(arr: readonly T[], seed: string, salt = ""): T =>
  arr[hash(`${salt}:${seed}`) % arr.length];

const ORG_ADJ = [
  "Northwind",
  "Brightpath",
  "Meridian",
  "Kestrel",
  "Silverline",
  "Oakmoor",
  "Harbour",
  "Crestview",
  "Beacon",
  "Foxglove",
  "Ironbridge",
  "Larkfield",
  "Quayside",
  "Westgate",
  "Amberley",
  "Cobalt",
] as const;
const ORG_NOUN = [
  "Logistics",
  "Retail",
  "Properties",
  "Industries",
  "Foods",
  "Distribution",
  "Holdings",
  "Manufacturing",
  "Estates",
  "Partners",
  "Group",
  "Storage",
] as const;
const ORG_SUFFIX = ["Ltd", "plc", "LLP", "Group", "Co"] as const;

const UTILITY = [
  "Northern Grid Networks",
  "Western Power Connections",
  "Pennine Distribution",
  "Coastal Energy Networks",
  "Central Grid Operations",
  "Highland Power Networks",
  "Severn Distribution",
  "Meadow Grid Networks",
] as const;

const FIRST = [
  "Alex",
  "Sam",
  "Jordan",
  "Taylor",
  "Morgan",
  "Casey",
  "Riley",
  "Jamie",
  "Cameron",
  "Drew",
  "Robin",
  "Quinn",
  "Harper",
  "Reese",
  "Avery",
  "Skylar",
] as const;
const LAST = [
  "Carter",
  "Bennett",
  "Hughes",
  "Coleman",
  "Patel",
  "Walsh",
  "Mercer",
  "Nolan",
  "Pearce",
  "Wrenn",
  "Sutton",
  "Okafor",
  "Lindqvist",
  "Marsh",
  "Doyle",
  "Aldridge",
] as const;

const STREET = [
  "Maple",
  "Elm",
  "Kingsway",
  "Riverside",
  "Hillcrest",
  "Brook",
  "Castle",
  "Orchard",
  "Sterling",
  "Victoria",
  "Pioneer",
  "Cathedral",
] as const;
const STREET_TYPE = ["Road", "Street", "Way", "Avenue", "Lane", "Park"] as const;
const TOWN = [
  "Ashford",
  "Redhill",
  "Daventry",
  "Wakefield",
  "Crawley",
  "Banbury",
  "Telford",
  "Rugby",
  "Swindon",
  "Warrington",
  "Bolton",
  "Dover",
  "Tamworth",
  "Basildon",
  "Corby",
  "Runcorn",
  "Wigan",
  "Newark",
  "Thurrock",
  "Lutterworth",
  "Avonmouth",
  "Doncaster",
  "Skelmersdale",
  "Trafford",
  "Hinckley",
  "Burton",
  "Wellingborough",
  "Kettering",
] as const;
const SITE_WORD = [
  "Distribution Centre",
  "Logistics Park",
  "Retail Park",
  "Industrial Estate",
  "Business Park",
  "Fulfilment Centre",
  "Trade Park",
  "Warehouse",
  "Commercial Park",
  "Enterprise Park",
  "Storage Depot",
  "Gateway Park",
  "Point",
  "Cross Docks",
] as const;

const POSTCODE_AREA = [
  "AB",
  "BS",
  "CV",
  "DA",
  "EX",
  "GL",
  "HR",
  "LN",
  "ME",
  "NN",
  "OX",
  "RG",
  "SN",
  "TN",
  "WA",
  "YO",
] as const;
const LETTERS = "ABDEFGHJLNPQRSTUWXYZ";

const fakeOrg = (real: string): string =>
  `${pick(ORG_ADJ, real, "oa")} ${pick(ORG_NOUN, real, "on")} ${pick(ORG_SUFFIX, real, "os")}`;

const fakeUtility = (real: string): string => pick(UTILITY, real, "util");

const fakePerson = (real: string): string =>
  `${pick(FIRST, real, "fn")} ${pick(LAST, real, "ln")}`;

const fakeSiteName = (real: string): string =>
  `${pick(TOWN, real, "tn")} ${pick(SITE_WORD, real, "sw")}`;

const fakeAddress = (real: string): string => {
  const num = (hash(`num:${real}`) % 250) + 1;
  return `${num} ${pick(STREET, real, "st")} ${pick(STREET_TYPE, real, "stt")}, ${pick(TOWN, real, "atn")}`;
};

const fakePostcode = (real: string): string => {
  const area = pick(POSTCODE_AREA, real, "pca");
  const inDigit = hash(`pcd1:${real}`) % 9;
  const outDigit = hash(`pcd2:${real}`) % 9;
  const l1 = LETTERS[hash(`pcl1:${real}`) % LETTERS.length];
  const l2 = LETTERS[hash(`pcl2:${real}`) % LETTERS.length];
  return `${area}${inDigit + 1} ${outDigit}${l1}${l2}`;
};

const fakeEmail = (real: string): string => {
  const first = pick(FIRST, real, "efn").toLowerCase();
  const last = pick(LAST, real, "eln").toLowerCase();
  const org = pick(ORG_ADJ, real, "edm").toLowerCase();
  return `${first}.${last}@${org}.example.com`;
};

const fakeMpan = (real: string): string => {
  let out = "";
  for (let i = 0; i < 13; i++) out += `${hash(`mpan${i}:${real}`) % 10}`;
  return out;
};

const fakeFilename = (real: string): string => {
  const dot = real.lastIndexOf(".");
  const ext = dot > 0 && dot < real.length - 1 ? real.slice(dot) : "";
  const docWords = [
    "Survey",
    "Lease",
    "Report",
    "Drawing",
    "Quote",
    "Statement",
    "Offer",
    "Schedule",
    "Datasheet",
    "Agreement",
  ] as const;
  const tag = `${(hash(`fn:${real}`) % 9000) + 1000}`;
  return `${pick(docWords, real, "fw")}-${tag}${ext}`;
};

const NOTE_KEYS: ReadonlySet<string> = new Set([
  "comments",
  "roof",
  "summary",
]);

interface ObjFlags {
  isCounterparty: boolean;
  isSiteContact: boolean;
  isProject: boolean;
  isBmaSite: boolean;
  isOrg: boolean;
  // The row's stable unique id, used to seed name fakes so distinct rows get
  // distinct (but per-row-stable) fake names even when their REAL names are
  // identical (e.g. several BMA sites all literally named the same).
  idSeed: string | null;
}

const classify = (o: Record<string, unknown>): ObjFlags => ({
  // Counterparty: a role plus contact_* fields (project_state.Counterparty).
  isCounterparty:
    "role" in o &&
    ("contact_name" in o || "contact_phone" in o || "contact_email" in o),
  // SiteContact: name + email + role, but no contact_* fields.
  isSiteContact: "role" in o && "email" in o && !("contact_email" in o),
  // The full Project model carries lifecycle_stage (Runs / Issues / messages
  // also have project_id, so key on the Project-only field, not project_id).
  isProject: "project_id" in o && "lifecycle_stage" in o,
  // BMA site (BmaSiteSummary / BmaSiteDetail): id + address + postcode.
  isBmaSite: "id" in o && "address" in o && "postcode" in o,
  // BMA organisation (BmaSiteOrganisation / BmaOrgSummary).
  isOrg: "bma_org_id" in o,
  idSeed:
    typeof o.project_id === "string"
      ? o.project_id
      : typeof o.id === "string"
        ? o.id
        : typeof o.bma_org_id === "string"
          ? o.bma_org_id
          : null,
});

const transformString = (
  key: string | null,
  val: string,
  parent: ObjFlags | null,
): string => {
  if (!val) return val;
  switch (key) {
    case "landlord":
    case "tenant":
    case "primary_epc":
    case "module_manufacturer":
      return fakeOrg(val);
    case "dno":
    case "idno":
    case "host_dno":
      return fakeUtility(val);
    case "designer":
    case "contact_name":
    case "pulled_by_name":
      return fakePerson(val);
    case "address":
      return fakeAddress(val);
    case "site_postcode":
    case "postcode":
      return fakePostcode(val);
    case "contact_email":
    case "email":
    case "owner_email":
    case "sender_email":
    case "pulled_from":
      return fakeEmail(val);
    case "updated_by":
      return DEMO_USER;
    case "mpan":
    case "import_mpan":
    case "export_mpan":
      return fakeMpan(val);
    case "original_name":
    case "filename":
      return fakeFilename(val);
    case "project_name":
      // Seed on the row's id so two projects with the same real name still get
      // distinct fake names (the picker showed every site as one name before).
      return fakeSiteName(parent?.idSeed ?? val);
    case "name":
      // Seed org / site names on the row's stable id (falling back to the
      // value) so identical real names don't collapse to one fake.
      if (parent?.isCounterparty) return fakeOrg(val);
      if (parent?.isOrg) return fakeOrg(parent?.idSeed ?? val);
      if (parent?.isSiteContact) return fakePerson(val);
      if (parent?.isProject || parent?.isBmaSite)
        return fakeSiteName(parent?.idSeed ?? val);
      return val;
    default:
      if (NOTE_KEYS.has(key ?? "")) return NOTES_PLACEHOLDER;
      return val;
  }
};

const transform = (
  value: unknown,
  key: string | null,
  parent: ObjFlags | null,
): unknown => {
  if (Array.isArray(value)) {
    // Arrays of bare MPAN strings (project_state.mpans, BmaSiteDetail.mpans).
    if (key === "mpans") {
      return value.map((v) => (typeof v === "string" ? fakeMpan(v) : transform(v, null, null)));
    }
    // Object elements classify themselves; string elements (citations,
    // known_gaps, *_ids) carry the array's key but match no PII rule.
    return value.map((v) =>
      v && typeof v === "object" ? transform(v, null, null) : transform(v, key, parent),
    );
  }
  if (value && typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const flags = classify(obj);
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) out[k] = transform(v, k, flags);
    return out;
  }
  if (typeof value === "string") return transformString(key, value, parent);
  return value;
};

// Recursively anonymise an API response. Caller gates on isDemoMode().
export const anonymizeData = <T>(value: T): T => transform(value, null, null) as T;
